import type { WorkflowAutomationCatalog } from './workflow-automation-authoring';

export interface WorkflowTimerAuthoringRuntime {
  producerEnabled: boolean;
  optionalStagesEnabled: boolean;
  optionalFormatExecutable: boolean;
}
export interface WorkflowTimerAuthoringSnapshot {
  contractVersion: 1;
  catalog: WorkflowAutomationCatalog;
  runtime: WorkflowTimerAuthoringRuntime;
}

const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const text = (value: unknown): value is string =>
  typeof value === 'string' && !!value.trim();
const positive = (value: unknown): value is number =>
  Number.isSafeInteger(value) && Number(value) > 0;
const hash = (value: unknown): boolean =>
  typeof value === 'string' && /^[a-f0-9]{64}$/.test(value);
const texts = (value: unknown): value is string[] =>
  Array.isArray(value) &&
  value.every(text) &&
  new Set(value).size === value.length;
const optional = (
  value: Record<string, unknown>,
  key: string,
  check: (value: unknown) => boolean,
): boolean =>
  !Object.prototype.hasOwnProperty.call(value, key) || check(value[key]);

function reference(value: unknown): boolean {
  if (!record(value) || !record(value['key'])) return false;
  const key = value['key'];
  return (
    ['provider', 'kind', 'code'].every((name) => text(key[name])) &&
    positive(key['version']) &&
    ['inputSchemaHash', 'outputSchemaHash', 'contractHash'].every((name) =>
      hash(value[name]),
    )
  );
}
function field(value: unknown): boolean {
  return (
    record(value) &&
    text(value['name']) &&
    text(value['type']) &&
    typeof value['required'] === 'boolean' &&
    typeof value['nullable'] === 'boolean' &&
    optional(value, 'maxLength', (item) => item === null || positive(item)) &&
    optional(value, 'maxItems', (item) => item === null || positive(item)) &&
    optional(value, 'values', (item) => item === null || texts(item)) &&
    optional(
      value,
      'items',
      (item) => item === null || (Array.isArray(item) && item.every(field)),
    ) &&
    optional(value, 'recordTypeCode', (item) => item === null || text(item))
  );
}
function schema(value: unknown, expectedHash: unknown): boolean {
  return (
    record(value) &&
    text(value['code']) &&
    positive(value['version']) &&
    hash(value['hash']) &&
    value['hash'] === expectedHash &&
    Array.isArray(value['fields']) &&
    value['fields'].every(field) &&
    new Set(value['fields'].map((item) => item['name'])).size ===
      value['fields'].length
  );
}

/** Admit one complete timer catalog without merging generic event contracts or rewriting its pins. */
export function workflowTimerAuthoringSnapshot(
  value: unknown,
): value is WorkflowTimerAuthoringSnapshot {
  if (
    !record(value) ||
    value['contractVersion'] !== 1 ||
    !record(value['runtime']) ||
    !record(value['catalog'])
  )
    return false;
  const runtime = value['runtime'];
  if (
    ![
      'producerEnabled',
      'optionalStagesEnabled',
      'optionalFormatExecutable',
    ].every((key) => typeof runtime[key] === 'boolean')
  )
    return false;
  const catalog = value['catalog'];
  const failurePolicies = catalog['failurePolicies'];
  if (
    catalog['contractVersion'] !== 1 ||
    !record(catalog['execution']) ||
    typeof catalog['execution']['supported'] !== 'boolean' ||
    !text(catalog['execution']['reasonCode']) ||
    !text(catalog['execution']['message']) ||
    !Array.isArray(catalog['triggerKinds']) ||
    catalog['triggerKinds'].length !== 1 ||
    catalog['triggerKinds'][0] !== 'scheduled-item' ||
    !texts(failurePolicies) ||
    failurePolicies.length !== 2 ||
    !['stop', 'continue'].every((code) => failurePolicies.includes(code)) ||
    !Array.isArray(catalog['contextBindings']) ||
    catalog['contextBindings'].length !== 5 ||
    !Array.isArray(catalog['operations'])
  )
    return false;
  const contexts: Record<string, [string, boolean, number | null]> = {
    'workflow.id': ['integer', false, null],
    'occurrence.id': ['integer', false, null],
    'subject.id': ['integer', true, null],
    'initiatingActor.id': ['string', false, 160],
    'workflow.etag': ['string', false, 256],
  };
  const found = new Set<string>();
  for (const binding of catalog['contextBindings']) {
    if (
      !record(binding) ||
      !text(binding['code']) ||
      !text(binding['label']) ||
      found.has(binding['code'])
    )
      return false;
    const expected = contexts[binding['code']];
    if (
      !expected ||
      binding['type'] !== expected[0] ||
      binding['nullable'] !== expected[1] ||
      binding['maxLength'] !== expected[2] ||
      !Array.isArray(binding['triggerKinds']) ||
      binding['triggerKinds'].length !== 1 ||
      binding['triggerKinds'][0] !== 'scheduled-item'
    )
      return false;
    found.add(binding['code']);
  }
  const commands = new Set<string>();
  for (const operation of catalog['operations']) {
    if (
      !record(operation) ||
      !reference(operation['command']) ||
      !record(operation['command']) ||
      !record(operation['command']['key']) ||
      operation['command']['key']['kind'] !== 'command' ||
      ![
        'label',
        'moduleCode',
        'lifecycle',
        'automationPermissionCode',
        'authorizationPolicy',
        'coordination',
      ].every((key) => text(operation[key])) ||
      !positive(operation['moduleVersion']) ||
      !hash(operation['moduleHash']) ||
      typeof operation['availableForNewUse'] !== 'boolean' ||
      typeof operation['packPermitted'] !== 'boolean' ||
      !(
        operation['unavailableReason'] === null ||
        text(operation['unavailableReason'])
      ) ||
      operation['caller'] !== 'scoped-system' ||
      !Array.isArray(operation['modes']) ||
      operation['modes'].length !== 1 ||
      operation['modes'][0] !== 'durable' ||
      !schema(operation['input'], operation['command']['inputSchemaHash']) ||
      !schema(operation['output'], operation['command']['outputSchemaHash']) ||
      ![
        'outcomes',
        'automationInputs',
        'requiredContext',
        'immediateEffects',
        'deferredEffects',
      ].every((key) => texts(operation[key])) ||
      !Array.isArray(operation['outcomes']) ||
      operation['outcomes'].length === 0 ||
      !Array.isArray(operation['requiredCapabilities']) ||
      !operation['requiredCapabilities'].every(reference) ||
      !optional(
        operation,
        'supportedAssignmentBases',
        (item) =>
          texts(item) &&
          item.length > 0 &&
          item.every((basis) => basis === 'queues' || basis === 'roles'),
      )
    )
      return false;
    const command = operation['command'];
    if (
      !['available', 'retired-for-new-use', 'suspended'].some(
        (state) => state === operation['lifecycle'],
      ) ||
      operation['availableForNewUse'] !==
        (operation['lifecycle'] === 'available' &&
          operation['packPermitted'] === true &&
          operation['unavailableReason'] === null)
    )
      return false;
    const key = operation['command']['key'];
    const identity = JSON.stringify([
      key['provider'],
      key['kind'],
      key['code'],
      key['version'],
    ]);
    if (commands.has(identity)) return false;
    const dependencies = new Map<string, Record<string, unknown>>();
    for (const dependency of operation['requiredCapabilities']) {
      if (!record(dependency) || !record(dependency['key'])) return false;
      const dependencyKey = dependency['key'];
      const dependencyIdentity = JSON.stringify([
        dependencyKey['provider'],
        dependencyKey['kind'],
        dependencyKey['code'],
        dependencyKey['version'],
      ]);
      if (dependencies.has(dependencyIdentity)) return false;
      dependencies.set(dependencyIdentity, dependency);
    }
    const root = dependencies.get(identity);
    if (
      !root ||
      !['inputSchemaHash', 'outputSchemaHash', 'contractHash'].every(
        (name) => root[name] === command[name],
      )
    )
      return false;
    commands.add(identity);
  }
  return true;
}
